import {
  DeviceSyncField,
  type DeviceSyncResponse,
  DEFAULT_DEVICE_SYNC_FIELDS,
} from '@am3nd-org/pointcraft-types';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';
import { DeviceSyncResponseCompatibilitySchema } from 'src/utils/operator-authentication-access';

import { useDeviceContextStore } from 'src/store/DeviceContext';
import { classifyAuthOperationError } from 'src/auth/errors/auth-operation-error';
import { useDeviceSyncStore, type DeviceSyncVersions } from 'src/store/DeviceSync';
import {
  getSessionGeneration,
  registerSessionCleanup,
  isSessionGenerationCurrent,
} from 'src/auth/session-lifecycle';

import {
  clearProviderCredentials,
  replaceProviderCredentials,
} from './provider-credentials.service';

export type DeviceSyncV4Field = DeviceSyncField;
export type DeviceSyncV4Payload = DeviceSyncResponse;
export type DeviceSyncV4Meta = DeviceSyncVersions;

export type BusinessDeviceSyncFields = {
  businessProfile?: unknown;
  userProfile?: unknown;
  terminals?: unknown;
  businessDevices?: unknown;
  providerDetails?: unknown;
  recurringPlans?: unknown;
};

const FIRESTORE_TO_SYNC_FIELD = {
  businessProfile: DeviceSyncField.BUSINESS_DATA,
  userProfile: DeviceSyncField.USER_DATA,
  terminals: DeviceSyncField.TERMINALS,
  businessDevices: DeviceSyncField.BUSINESS_DEVICES,
  providerDetails: DeviceSyncField.PROVIDER_DETAILS,
  recurringPlans: DeviceSyncField.RECURRING_PLANS,
} as const;

const SYNC_FIELD_TO_RESPONSE_KEY: Record<DeviceSyncField, keyof DeviceSyncResponse> = {
  [DeviceSyncField.APPLICATION_DEVICE_CONTEXT]: 'applicationDeviceContext',
  [DeviceSyncField.BUSINESS_DATA]: 'businessData',
  [DeviceSyncField.USER_DATA]: 'userData',
  [DeviceSyncField.TERMINALS]: 'terminals',
  [DeviceSyncField.BUSINESS_DEVICES]: 'businessDevices',
  [DeviceSyncField.PROVIDER_DETAILS]: 'providerDetails',
  [DeviceSyncField.PROVIDER_CREDENTIALS]: 'providerCredentials',
  [DeviceSyncField.RECURRING_PLANS]: 'recurringPlans',
};

type SyncTarget = { version?: number; force: boolean };
type SyncWork = {
  generation: number;
  tenantId: string | null;
  pending: Map<DeviceSyncField, SyncTarget>;
  inFlight: Map<DeviceSyncField, SyncTarget>;
  active: Promise<void> | null;
  cooldownUntil: number;
  lastError: unknown;
  cancelDelay?: () => void;
};
let work: SyncWork | null = null;

function isCurrent(state: SyncWork): boolean {
  return (
    work === state &&
    isSessionGenerationCurrent(state.generation) &&
    useDeviceSyncStore.getState().tenantId === state.tenantId
  );
}

registerSessionCleanup(() => {
  const retired = work;
  work = null;
  retired?.pending.clear();
  retired?.cancelDelay?.();
});

function versionKeyFor(field: DeviceSyncField): keyof DeviceSyncVersions | undefined {
  return (Object.keys(FIRESTORE_TO_SYNC_FIELD) as Array<keyof DeviceSyncVersions>).find(
    (key) => FIRESTORE_TO_SYNC_FIELD[key] === field
  );
}

function mergeTarget(previous: SyncTarget | undefined, next: SyncTarget): SyncTarget {
  let version = previous?.version ?? next.version;
  if (previous?.version !== undefined && next.version !== undefined) {
    version = Math.max(previous.version, next.version);
  }
  return {
    force: Boolean(previous?.force || next.force),
    version,
  };
}

function retryDelay(state: SyncWork, milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      if (state.cancelDelay === finish) state.cancelDelay = undefined;
      resolve();
    };
    const timer = setTimeout(finish, milliseconds);
    state.cancelDelay = finish;
  });
}

function normalizeFields(fields?: DeviceSyncField[]): DeviceSyncField[] {
  const requested = fields?.length ? fields : DEFAULT_DEVICE_SYNC_FIELDS;
  return Array.from(new Set(requested));
}

function hasOwn(value: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function readVersion(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { version } = value as { version?: unknown };
  return typeof version === 'number' && Number.isFinite(version) && version >= 0
    ? version
    : undefined;
}

function buildDeviceSyncUrl(fields: DeviceSyncField[]): string {
  const params = new URLSearchParams({ fields: fields.join(',') });
  return `${apiEndpoints.v4.deviceSync}?${params.toString()}`;
}

export async function fetchDeviceSyncPayload(
  fields: DeviceSyncField[]
): Promise<DeviceSyncResponse> {
  const result = await apiSenderV2(buildDeviceSyncUrl(fields), ApiMethods.GET, null, true, null, {
    throwOnError: true,
    timeout: 30_000,
  });
  const parsed = DeviceSyncResponseCompatibilitySchema.safeParse(result);

  if (!parsed.success) {
    throw new Error(`[DeviceSyncV4] Invalid response: ${parsed.error.message}`);
  }

  const missingFields = fields.filter(
    (field) => !hasOwn(parsed.data, SYNC_FIELD_TO_RESPONSE_KEY[field])
  );
  if (missingFields.length) {
    throw new Error(`[DeviceSyncV4] Response missing fields: ${missingFields.join(', ')}`);
  }

  return parsed.data;
}

async function applyDeviceSyncPayload(payload: DeviceSyncResponse, state: SyncWork): Promise<void> {
  if (!isCurrent(state)) return;
  const currentTenantId = useDeviceSyncStore.getState().tenantId;
  const responseTenantId =
    payload.businessData?.id ?? payload.applicationDeviceContext?.businessId ?? currentTenantId;

  if (currentTenantId && responseTenantId && currentTenantId !== responseTenantId) {
    useDeviceSyncStore.getState().clearTenantData();
    useDeviceContextStore.getState().reset();
    state.tenantId = null;
    await clearProviderCredentials();
    if (!isCurrent(state)) return;
  }

  if (payload.applicationDeviceContext) {
    useDeviceContextStore.getState().setApplicationDeviceContext(payload.applicationDeviceContext);
    useDeviceContextStore.getState().setStatus('ready');
  }

  if (payload.providerCredentials) {
    await replaceProviderCredentials(payload.providerCredentials, () => isCurrent(state));
    if (!isCurrent(state)) return;
  }

  const safeSlices: DeviceSyncResponse = {
    ...(payload.businessData !== undefined ? { businessData: payload.businessData } : {}),
    ...(payload.userData !== undefined ? { userData: payload.userData } : {}),
    ...(payload.terminals !== undefined ? { terminals: payload.terminals } : {}),
    ...(payload.businessDevices !== undefined ? { businessDevices: payload.businessDevices } : {}),
    ...(payload.providerDetails !== undefined ? { providerDetails: payload.providerDetails } : {}),
    ...(payload.recurringPlans !== undefined ? { recurringPlans: payload.recurringPlans } : {}),
  };

  if (Object.keys(safeSlices).length) {
    useDeviceSyncStore.getState().applySlices(safeSlices);
    state.tenantId = useDeviceSyncStore.getState().tenantId;
  }
}

async function runSync(state: SyncWork): Promise<void> {
  while (isCurrent(state) && state.pending.size) {
    const targets = state.pending;
    state.pending = new Map();
    state.inFlight = targets;
    const fields = Array.from(targets.keys());
    const versions: DeviceSyncVersions = {};
    targets.forEach((target, field) => {
      const key = versionKeyFor(field);
      if (key && target.version !== undefined) versions[key] = target.version;
    });
    useDeviceSyncStore.getState().setStatus('syncing');
    let completed = false;
    for (let attempt = 0; attempt < 3 && isCurrent(state); attempt += 1) {
      try {
        // Each request captures its targets. A newer event remains in pending.
        // eslint-disable-next-line no-await-in-loop
        const payload = await fetchDeviceSyncPayload(fields);
        if (!isCurrent(state)) return;
        // eslint-disable-next-line no-await-in-loop
        await applyDeviceSyncPayload(payload, state);
        if (!isCurrent(state)) return;
        useDeviceSyncStore.getState().setVersions(versions);
        useDeviceSyncStore.getState().setStatus('ready');
        completed = true;
        break;
      } catch (error) {
        if (!isCurrent(state)) return;
        const failure = classifyAuthOperationError(error);
        const transient = ['NETWORK', 'TIMEOUT', 'SERVER'].includes(failure.kind);
        if (transient && attempt < 2) {
          // eslint-disable-next-line no-await-in-loop
          await retryDelay(state, [2_000, 10_000][attempt] + Math.floor(Math.random() * 250));
        } else {
          if (failure.kind === 'RATE_LIMIT') {
            state.cooldownUntil = Date.now() + (failure.retryAfterMs ?? 60_000);
          }
          state.lastError = error;
          targets.forEach((target, field) => {
            state.pending.set(field, mergeTarget(state.pending.get(field), target));
          });
          useDeviceSyncStore
            .getState()
            .setStatus('error', error instanceof Error ? error.message : 'Device sync failed');
          throw error;
        }
      }
    }
    state.inFlight.clear();
    if (!completed) return;
  }
}

/** Coalesce duplicate targets and fetch newer targets after the current publication. */
export async function syncDeviceSyncV4Fields(
  fields: DeviceSyncField[] = DEFAULT_DEVICE_SYNC_FIELDS,
  remoteVersions: DeviceSyncVersions = {},
  options: { force?: boolean } = {}
): Promise<void> {
  if (!work || !isCurrent(work)) {
    work?.cancelDelay?.();
    work = {
      generation: getSessionGeneration(),
      tenantId: useDeviceSyncStore.getState().tenantId,
      pending: new Map(),
      inFlight: new Map(),
      active: null,
      cooldownUntil: 0,
      lastError: null,
    };
  }
  const state = work;
  const requestedVersions: DeviceSyncVersions = Object.fromEntries(
    Object.entries(remoteVersions).filter(
      ([, version]) => typeof version === 'number' && Number.isFinite(version) && version >= 0
    )
  );
  const newerVersionRequested = fields.some((field) => {
    const key = versionKeyFor(field);
    const version = key ? requestedVersions[key] : undefined;
    const running = state.inFlight.get(field);
    return version !== undefined && running && version > (running.version ?? -1);
  });
  normalizeFields(fields).forEach((field) => {
    const key = versionKeyFor(field);
    const version = key ? requestedVersions[key] : undefined;
    const target = { version, force: options.force ?? false };
    const applied = key ? useDeviceSyncStore.getState().versions[key] : undefined;
    if (!target.force && version !== undefined && applied !== undefined && version <= applied)
      return;
    const running = state.inFlight.get(field);
    if (
      !target.force &&
      running &&
      ((version === undefined && !newerVersionRequested) ||
        (version !== undefined && running.version !== undefined && version <= running.version))
    )
      return;
    state.pending.set(field, mergeTarget(state.pending.get(field), target));
  });
  if (state.active) return state.active;
  if (!state.pending.size) return Promise.resolve();
  if (Date.now() < state.cooldownUntil) throw state.lastError;
  // Yield once to combine simultaneous startup/listener requests.
  const active = Promise.resolve()
    .then(() => runSync(state))
    .finally(() => {
      if (state.active === active) {
        state.active = null;
        state.inFlight.clear();
      }
    });
  state.active = active;
  return active;
}

export async function hydrateDeviceSyncV4FromCache(): Promise<boolean> {
  if (!useDeviceSyncStore.persist.hasHydrated()) {
    await useDeviceSyncStore.persist.rehydrate();
  }
  return Boolean(useDeviceSyncStore.getState().cachedAt);
}

export async function handleDeviceSyncBusinessFields(
  fields: BusinessDeviceSyncFields
): Promise<void> {
  const remoteVersions: DeviceSyncVersions = {
    businessProfile: readVersion(fields.businessProfile),
    userProfile: readVersion(fields.userProfile),
    terminals: readVersion(fields.terminals),
    businessDevices: readVersion(fields.businessDevices),
    providerDetails: readVersion(fields.providerDetails),
    recurringPlans: readVersion(fields.recurringPlans),
  };
  const localVersions = useDeviceSyncStore.getState().versions;

  const fieldsToSync = (
    Object.entries(FIRESTORE_TO_SYNC_FIELD) as Array<[keyof DeviceSyncVersions, DeviceSyncField]>
  )
    .filter(([versionKey]) => {
      const remoteVersion = remoteVersions[versionKey];
      return typeof remoteVersion === 'number' && remoteVersion > (localVersions[versionKey] ?? -1);
    })
    .map(([, field]) => field);

  if (!fieldsToSync.length) return;

  const isPairedDevice = Boolean(useDeviceContextStore.getState().getApplicationDeviceContext());
  const shouldRefreshProviderCredentials =
    isPairedDevice && fieldsToSync.includes(DeviceSyncField.PROVIDER_DETAILS);
  if (isPairedDevice && fieldsToSync.includes(DeviceSyncField.BUSINESS_DEVICES)) {
    fieldsToSync.push(DeviceSyncField.APPLICATION_DEVICE_CONTEXT);
  }

  if (shouldRefreshProviderCredentials) fieldsToSync.push(DeviceSyncField.PROVIDER_CREDENTIALS);
  await syncDeviceSyncV4Fields(Array.from(new Set(fieldsToSync)), remoteVersions);
}
